import { createContext, useContext, useEffect, useState, ReactNode } from "react";

type Theme = "dark" | "light";

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType>({ theme: "dark", toggleTheme: () => {} });

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem("bhg-theme-v2");
    // Default to the Apple-style light theme; dark is opt-in via the toggle.
    return stored === "dark" ? "dark" : "light";
  });

  useEffect(() => {
    const root = document.documentElement;
    // Toggle .dark alongside .light. The CSS tokens only need .light, but
    // tailwind.config sets darkMode: ["class"], which keys every dark: utility
    // off a .dark class. Until 2026-09-13 nothing ever added it, so dark:
    // text colors across the app (blog callouts, price change colors, badge
    // chips) silently never applied for dark-theme visitors. (prose-invert is
    // still inert: @tailwindcss/typography is installed but not registered as
    // a plugin in tailwind.config.ts.)
    root.classList.toggle("light", theme === "light");
    root.classList.toggle("dark", theme === "dark");
    localStorage.setItem("bhg-theme-v2", theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === "dark" ? "light" : "dark");

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
